import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { IconCoins, IconChevronRight, IconX } from '@tabler/icons-react';
import { toBnDigits } from '../../utils/bnFormat';
import { useBuyCreditPath } from '../../store/paymentConfigStore';

/**
 * Low-credit warning banner shown at the top of generation/template pages.
 * Renders when the (owner's) effective credit is 10 or less. When the balance is
 * negative the user is in "advance" mode — the shortfall (debt) is auto-adjusted
 * on the next recharge, so we show a distinct message.
 *
 * @param {number} credit - effective credit available to the user (may be negative).
 * @param {string} [className] - optional wrapper classes (padding around the card).
 */
// `showBuyButton` — set false on pages that ARE the buy-credit page (the CTA would just
// link back to the same place).
export default function LowCreditBanner({ credit = 0, className = 'px-3 sm:px-6 pt-3', showBuyButton = true }) {
    const [closed, setClosed] = useState(false);
    const { t, i18n } = useTranslation();
    const buyCreditPath = useBuyCreditPath();

    if (closed || credit > 10) return null;

    const inAdvance = credit < 0; // balance is negative → using advance allowance
    // Banner copy is Bangla, so localize the numeral too when the UI is in Bangla.
    const creditDisplay = i18n.language?.startsWith('bn') ? toBnDigits(credit) : credit;

    return (
        <div className={className}>
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-red-200 dark:border-red-800/60 bg-gradient-to-r from-red-100 to-red-50 dark:from-red-950/50 dark:to-red-900/30 p-4 pr-10 sm:pr-8 shadow-sm">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-red-500 text-white dark:bg-red-600 flex items-center justify-center">
                        <IconCoins size={22} />
                    </span>
                    <div className="min-w-0">
                        {inAdvance ? (
                            <>
                                <p className="text-sm font-bold text-red-700 dark:text-red-300">{t('payments.lowCredit.advanceTitle')}</p>
                                <p className="mt-0.5 text-sm text-red-800/80 dark:text-red-200/70">
                                    <Trans
                                        i18nKey="payments.lowCredit.advanceMessage"
                                        values={{ count: creditDisplay }}
                                        components={{ strong: <strong className="font-bold text-red-600 dark:text-red-400" /> }}
                                    />
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-bold text-red-700 dark:text-red-300">{t('payments.lowCredit.lowTitle')}</p>
                                <p className="mt-0.5 text-sm text-red-800/80 dark:text-red-200/70">
                                    <Trans
                                        i18nKey="payments.lowCredit.lowMessage"
                                        values={{ count: creditDisplay }}
                                        components={{ strong: <strong className="font-bold text-red-600 dark:text-red-400" /> }}
                                    />
                                </p>
                            </>
                        )}
                    </div>
                </div>
                {showBuyButton && (
                    <Link
                        to={buyCreditPath}
                        className="flex-shrink-0 inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors w-full sm:w-auto"
                    >
                        {t('payments.lowCredit.buyButton')}
                        <IconChevronRight size={14} />
                    </Link>
                )}
                <button
                    onClick={() => setClosed(true)}
                    className="absolute right-3 top-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    aria-label={t('payments.lowCredit.close')}
                >
                    <IconX size={16} />
                </button>
            </div>
        </div>
    );
}
